import { useEffect, useState } from 'react';

import { IconLoader2, IconRefresh, IconReload } from '@tabler/icons-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import MainCard from '@/components/MainCard';
import { StatCard } from '@/components/cards';
import { formatDateTime } from '@/utils/dateFormat';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ChurnExecution {
  id: number;
  i_customer: number;
  msisdn: string;
  iccid: string;
  wallet_account_number: string;
  temporal_workflow_id: string;
  status: string;
  error_message: string;
  error_step: string;
  attempt_count: number;
  last_attempt_at: string;
  wallet_balance: string | null;
  step_portaone_list: string;
  step_portaone_terminate: string;
  step_portaone_rename: string;
  step_db_update: string;
  step_kustomer_get: string;
  step_kustomer_update: string;
  step_soa_deactivate: string;
  step_soa_webhook_received: string;
  step_wallet_check_balance: string;
  step_wallet_remove_balance: string;
  step_wallet_close_account: string;
}

export function ChurnFailedExecutionsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executions, setExecutions] = useState<ChurnExecution[]>([]);
  const [retrying, setRetrying] = useState<number | null>(null);

  const fetchFailedExecutions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/churn/executions/failed');
      const data = response.data?.data || [];
      setExecutions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to load failed churn executions', err);
      setError(err.response?.data?.message || 'Failed to load failed churn executions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFailedExecutions();
  }, []);

  const handleRefresh = () => {
    fetchFailedExecutions();
  };

  const handleRetry = async (executionId: number) => {
    setRetrying(executionId);
    try {
      await api.post(`/churn/executions/${executionId}/retry`);
      toast.success(`Retry initiated for execution ${executionId}`);
      // Refresh the list after a short delay
      setTimeout(() => {
        fetchFailedExecutions();
      }, 1000);
    } catch (err: any) {
      console.error(`Failed to retry execution ${executionId}`, err);
      toast.error(err.response?.data?.message || 'Failed to retry execution');
    } finally {
      setRetrying(null);
    }
  };

  const totalWithBalance = executions.filter(
    (e) => e.wallet_balance && parseFloat(e.wallet_balance) > 0
  ).length;

  const totalBalance = executions.reduce((sum, e) => {
    return sum + (e.wallet_balance ? parseFloat(e.wallet_balance) : 0);
  }, 0);

  return (
    <TooltipProvider>
      <div>
        <PageHeader
          title="Failed Churn Executions"
          subtitle="Monitor and troubleshoot failed churn workflow executions"
          action={
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <IconRefresh data-icon="inline-start" />
              Refresh
            </Button>
          }
        />

        {executions.length > 0 && (
          <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
            <StatCard title="Total Failed" value={executions.length.toLocaleString()} />
            <StatCard title="With Wallet Balance" value={totalWithBalance.toLocaleString()} />
            <StatCard title="Total Balance" value={`R ${totalBalance.toFixed(2)}`} />
            <StatCard
              title="Avg Attempts"
              value={(executions.reduce((sum, e) => sum + e.attempt_count, 0) / executions.length).toFixed(1)}
            />
          </div>
        )}

        <MainCard>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <IconLoader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
            </div>
          )}

          {!loading && error && (
            <div className="p-3">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}

          {!loading && !error && executions.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>MSISDN</TableHead>
                  <TableHead>Workflow ID</TableHead>
                  <TableHead>Error Step</TableHead>
                  <TableHead>Error Message</TableHead>
                  <TableHead className="text-right">Attempts</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Last Attempt</TableHead>
                  <TableHead>Failed Steps</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {executions.map((exec) => {
                  const failedSteps = [
                    exec.step_portaone_list === 'FAILED' && 'PortaOne List',
                    exec.step_portaone_terminate === 'FAILED' && 'PortaOne Terminate',
                    exec.step_portaone_rename === 'FAILED' && 'PortaOne Rename',
                    exec.step_db_update === 'FAILED' && 'DB Update',
                    exec.step_kustomer_get === 'FAILED' && 'Kustomer Get',
                    exec.step_kustomer_update === 'FAILED' && 'Kustomer Update',
                    exec.step_soa_deactivate === 'FAILED' && 'SOA Deactivate',
                    exec.step_wallet_check_balance === 'FAILED' && 'Wallet Check',
                    exec.step_wallet_remove_balance === 'FAILED' && 'Wallet Remove',
                    exec.step_wallet_close_account === 'FAILED' && 'Wallet Close',
                  ].filter(Boolean);

                  return (
                    <TableRow key={exec.id}>
                      <TableCell>{exec.id}</TableCell>
                      <TableCell>{exec.i_customer}</TableCell>
                      <TableCell>{exec.msisdn}</TableCell>
                      <TableCell className="max-w-[250px]">
                        <div
                          className="truncate font-mono text-[0.85rem]"
                          title={exec.temporal_workflow_id}
                        >
                          {exec.temporal_workflow_id}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="destructive">{exec.error_step}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        <div className="truncate" title={exec.error_message}>
                          {exec.error_message}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{exec.attempt_count}</TableCell>
                      <TableCell className="text-right">
                        {exec.wallet_balance ? `R ${parseFloat(exec.wallet_balance).toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell>{formatDateTime(exec.last_attempt_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-row flex-wrap gap-1">
                          {failedSteps.length > 0 ? (
                            failedSteps.map((step, idx) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className="border-red-200 text-red-700"
                              >
                                {step}
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline">No step failures</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8"
                                onClick={() => handleRetry(exec.id)}
                                disabled={retrying === exec.id}
                                aria-label="Retry workflow execution"
                              />
                            }
                          >
                            {retrying === exec.id ? (
                              <IconLoader2 className="size-5 animate-spin" aria-hidden />
                            ) : (
                              <IconReload className="size-4" aria-hidden />
                            )}
                          </TooltipTrigger>
                          <TooltipContent>Retry workflow execution</TooltipContent>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {!loading && !error && executions.length === 0 && (
            <div className="p-3 text-center">
              <Alert variant="success">
                <AlertDescription className="text-inherit">
                  No failed churn executions found
                </AlertDescription>
              </Alert>
            </div>
          )}
        </MainCard>
      </div>
    </TooltipProvider>
  );
}
